import React, { useEffect, useState, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { Dropdown, Menu, Modal, Tooltip } from 'antd';
import Icon, { MoreOutlined } from '@ant-design/icons';
import { DragIcon } from 'static/img/svg/SVGs';
import { FormattedMessage } from 'react-intl';
import withTranslations from 'app/components/HOGs/withTranslations';
// drag and drop
import { useDrag, useDrop } from 'react-dnd';
import { applyingTypes, applyingPrivate } from 'app/constants/folder.constants';
import FolderInput, { warnings } from './FolderInput';
import {
  resortFolders,
  resortPlaceFolders,
} from '../../../../actions/Menu/folders.actions';
import {
  deleteFolder,
  renameFolder,
} from '../../../../actions/Menu/folders-async.actions';
import { saveFolderOrder } from '../../../../API/folders.api';
import TourCounter from './tourCounter';

const { confirm } = Modal;

const folderMenu = (clickHandler) => {
  const items = [
    {
      label: <FormattedMessage id="Rename" defaultMessage="Rename" />,
      key: 'rename',
    },
    {
      label: <FormattedMessage id="Remove" defaultMessage="Remove" />,
      key: 'remove',
    },
  ];
  return <Menu onClick={clickHandler} items={items} />;
};

const TourFolder = (props) => {
  const {
    id,
    name,
    tourCounter,
    folderNameList,
    isMobile,
    index,
    setDraggingId,
    tr,
    applying,
  } = props;
  const dispatch = useDispatch();
  const [title, setTitle] = useState(name);
  const [renaming, toggleRenaming] = useState(false);
  const [warning, changeWarning] = useState(warnings.empty);
  const folderRef = useRef(null);
  const titleRef = useRef(null);
  const folderTitleRef = useRef(null);

  const showConfirm = () => {
    confirm({
      title: tr['Do you want to delete this folder'],
      content: tr['Tours will not be removed'],
      okText: tr.Yes,
      cancelText: tr.No,
      onOk() {
        dispatch(deleteFolder(id, applying));
      },
    });
  };

  const chooseMenu = ({ key }) => {
    switch (key) {
      case 'rename':
        toggleRenaming(true);
        break;

      case 'remove':
        showConfirm();
        break;

      default:
        break;
    }
  };

  const finishRenaming = (e) => {
    if (e.key === 'Enter' && !warning.show && title) {
      dispatch(renameFolder(id, title));
      toggleRenaming(false);
      changeWarning(warnings.empty);
    } else if (e.key === 'Escape') {
      setTitle(name);
      toggleRenaming(false);
      changeWarning(warnings.empty);
    }
  };

  const changeFolderName = (value) => {
    setTitle(value);
    if (value.length > 255) {
      changeWarning(warnings.maxLength);
    } else if (folderNameList.includes(value)) {
      changeWarning(warnings.wrongName);
    } else {
      changeWarning(warnings.empty);
    }
  };

  // drag'n'drop
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'FOLDER',
    item: () => ({ id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => saveFolderOrder(applying),
  });

  const [{ handlerId }, drop] = useDrop({
    accept: 'FOLDER',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!folderRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = folderRef.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      if (+applying === applyingTypes.PLACE_FOLDER) {
        dispatch(resortPlaceFolders(item.id, hoverIndex));
      } else {
        dispatch(resortFolders(item.id, hoverIndex));
      }
      item.index = hoverIndex;
    },
  });

  useEffect(() => {
    setDraggingId(isDragging ? id : false);
  }, [isDragging]);
  // end drag'n'drop

  const cutTitle =
    titleRef.current?.offsetWidth > folderTitleRef.current?.offsetWidth ||
    titleRef.current?.offsetHeight > folderTitleRef.current?.clientHeight;

  const tourFolderClass = applyingPrivate(applying)
    ? 'tour-folder'
    : 'tour-folder dream';

  return (
    <>
      <div
        className={tourFolderClass}
        ref={drop(preview(folderRef))}
        data-handler-id={handlerId}
      >
        {renaming && !isMobile ? (
          <FolderInput
            value={title}
            onKeyDown={finishRenaming}
            onChange={changeFolderName}
            warning={warning}
            isMobile={false}
          />
        ) : (
          <>
            {applyingPrivate(applying) ? (
              <div className="drag-icon-indicator" ref={drag}>
                <Icon
                  component={DragIcon}
                  fill="#7c7c7c"
                  height="15px"
                  style={{ color: '#7c7c7c', fontSize: '15px' }}
                />
              </div>
            ) : null}
            {cutTitle ? (
              <Tooltip title={name} align={{ offset: [0, 10] }}>
                <span ref={folderTitleRef} className="folder-title">
                  <span ref={titleRef}>{name}</span>
                </span>
              </Tooltip>
            ) : (
              <span ref={folderTitleRef} className="folder-title">
                <span ref={titleRef}>{name}</span>
              </span>
            )}
            {applyingPrivate(applying) ? (
              <TourCounter count={tourCounter} />
            ) : null}
            {applyingPrivate(applying) ? (
              <Dropdown overlay={folderMenu(chooseMenu)} trigger={['click']}>
                <MoreOutlined />
              </Dropdown>
            ) : null}
          </>
        )}
      </div>
      <Modal
        title={tr['Input folder name']}
        visible={renaming && isMobile}
        okText={tr.Save}
        cancelText={tr.Cancel}
        onOk={() => finishRenaming({ key: 'Enter' })}
        onCancel={() => finishRenaming({ key: 'Escape' })}
        bodyStyle={{ paddingTop: '10px' }}
        className="folder-title-modal"
      >
        <FolderInput
          value={title}
          onChange={changeFolderName}
          onKeyDown={() => {}}
          warning={warning}
          isMobile
        />
      </Modal>
    </>
  );
};

TourFolder.propTypes = {
  id: PropTypes.number,
  name: PropTypes.string.isRequired,
  folderNameList: PropTypes.arrayOf(PropTypes.string),
};

const Card = memo(TourFolder);

export default withTranslations(Card);
